import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { MaintenanceGate } from './components/MaintenanceGate'
import './index.css'
import {
  checkAndClearCacheOnUpdate,
  checkForRemoteVersionUpdate,
  disableLegacyServiceWorkers,
  ensureFreshDevClient,
} from './lib/utils'
import { installNumberInputWheelGuard } from './lib/numberInputWheelGuard'
import { installClientErrorReporter, reportClientError } from './lib/clientErrorReporter'

// Создаем QueryClient для React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 минут кэш по умолчанию
      gcTime: 10 * 60 * 1000, // 10 минут хранение в кэше (ранее cacheTime)
      refetchOnWindowFocus: false, // Не перезагружать при фокусе окна
      retry: 1, // Повторять запрос 1 раз при ошибке
    },
  },
})

// Проверяем версию и автоматически очищаем кэш при необходимости
disableLegacyServiceWorkers()
checkAndClearCacheOnUpdate()
checkForRemoteVersionUpdate()
installNumberInputWheelGuard()

// Инициализируем мок Telegram WebApp для локальной разработки
if (import.meta.env.DEV) {
  // Импортируем и инициализируем мок до загрузки приложения
  import('./lib/mockTelegram').then(({ initMockTelegramWebApp }) => {
    initMockTelegramWebApp()
  })
  // Import debug utility
  import('./lib/debugAuth')
}

// Clear cache if ?clear=1 parameter is present OR startapp contains 'clear'
const shouldClearCache = 
  location.search.includes('clear=1') ||
  location.hash.includes('clear=1') ||
  (window.Telegram?.WebApp?.initData && 
   window.Telegram.WebApp.initData.includes('start_param=clear'));

if (shouldClearCache) {
  console.log('🧹 Hard reset via URL parameter or Telegram startapp');

  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('clear');
  cleanUrl.hash = cleanUrl.hash.replace(/[&#]?clear=1/, '').replace(/^#&/, '#');

  import('./lib/utils').then(({ clearAppCacheWithProgress }) => {
    // TODO: раскомментировать после настройки роли администратора,
    // чтобы ?clear=1 не выбрасывал обычных пользователей из авторизации
    // clearAppCacheWithProgress({ preserveAuthToken: true, reload: false }).finally(() => {
    clearAppCacheWithProgress({ reload: false }).finally(() => {
      window.location.replace(cleanUrl.toString());
    });
  });
}

// Disable zoom on mobile
document.addEventListener('touchstart', (e) => {
  if (e.touches.length > 1) {
    e.preventDefault()
  }
})

let lastTouchEnd = 0
document.addEventListener('touchend', (e) => {
  const now = new Date().getTime()
  if (now - lastTouchEnd <= 300) {
    e.preventDefault()
  }
  lastTouchEnd = now
}, false)

// Prevent context menu on long press
document.addEventListener('contextmenu', (e) => {
  e.preventDefault()
})

// Глобальные ошибки, сбои и медленные запросы к Supabase → журнал /management/health.
installClientErrorReporter()

// Создаем fallback UI на случай критической ошибки
const createFallbackUI = (error: any) => {
  document.body.innerHTML = `
    <div style="
      position: fixed; 
      top: 0; left: 0; right: 0; bottom: 0; 
      background: #fff; 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
      justify-content: center; 
      padding: 20px; 
      font-family: Arial, sans-serif;
      z-index: 99999;
    ">
      <div style="text-align: center; max-width: 400px;">
        <div style="font-size: 48px; margin-bottom: 20px;">🚨</div>
        <h1 style="color: #dc3545; margin-bottom: 20px;">Критическая ошибка</h1>
        <p style="color: #666; margin-bottom: 30px;">
          Приложение не смогло загрузиться. Попробуйте перезагрузить страницу.
        </p>
        <div style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;">
          <button onclick="window.location.reload()" style="
            background: #007bff; 
            color: white; 
            border: none; 
            padding: 12px 20px; 
            border-radius: 6px; 
            cursor: pointer;
            font-size: 14px;
          ">
            🔄 Перезагрузить
          </button>
        </div>
        <details style="margin-top: 30px; text-align: left;">
          <summary style="cursor: pointer; color: #666;">Технические детали</summary>
          <pre style="
            background: #f8f9fa; 
            padding: 15px; 
            border-radius: 4px; 
            overflow: auto; 
            font-size: 12px; 
            margin-top: 10px;
            border: 1px solid #dee2e6;
          ">${error?.toString() || 'Unknown error'}</pre>
        </details>
      </div>
    </div>
  `
}

void (async () => {
  try {
    await ensureFreshDevClient()

    const root = ReactDOM.createRoot(document.getElementById('root')!)
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <MaintenanceGate>
            <App />
          </MaintenanceGate>
        </QueryClientProvider>
      </React.StrictMode>
    )
  } catch (error) {
    reportClientError({ kind: 'js_error', message: `запуск: ${String(error)}`, source: 'main.tsx' })
    createFallbackUI(error)
  }
})()
